import { useMemo } from 'react';
import { ReportBuilderFilters, ReportBuilderClientData } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import {
  getChildrenDivisionsIds,
  getDivision,
  getMostGranularLevel,
  getSiblingsDivisionsTreeIds,
} from 'screens/WidgetGenerator/reportGeneratorComponents/helpers/division';
import { DivisionTree } from '@zencity/survey-types';
import { DEFAULT_DEPTH_LEVEL_TO_GROUP_BY } from 'services/answerClassificationBreakdown';

interface Props {
  filters: ReportBuilderFilters;
  clientData: ReportBuilderClientData;
}
export const useDivisionTree = ({ filters, clientData }: Props): DivisionTree[] | undefined =>
  useMemo(() => {
    if (!filters?.surveyGroupId) {
      return undefined;
    }
    const selectedSurveyGroup = clientData.surveysGroups.find(
      (surveyGroup) => surveyGroup.id === filters.surveyGroupId,
    );

    if (!selectedSurveyGroup) {
      return undefined;
    }

    return clientData.divisionTreeBySurveyType[selectedSurveyGroup.type];
  }, [filters?.surveyGroupId, clientData.surveysGroups, clientData.divisionTreeBySurveyType]);

export const useDivisionIdsToFilterBy = ({ filters, clientData }: Props): number[] => {
  const divisionTree = useDivisionTree({ filters, clientData });

  return useMemo<number[]>(() => {
    if (!filters?.division || !divisionTree) {
      return [];
    }

    if (filters?.displayDivisionSiblings) {
      return getSiblingsDivisionsTreeIds(filters.division, divisionTree);
    }

    return getChildrenDivisionsIds(filters.division, divisionTree, true);
  }, [filters?.division, filters?.displayDivisionSiblings, divisionTree]);
};

/**
 * This hook returns a `string` representation of the depth level instead of
 * `number` because when the depth level is `0` then it is automatically
 * considered a falsy value, which cause issues passing it on.
 */
export const useGroupByDepthLevel = ({ filters, clientData }: Props): string | undefined => {
  const divisionTree = useDivisionTree({ filters, clientData });

  return useMemo<string | undefined>(() => {
    if (!divisionTree || divisionTree.length < 1) {
      // When division layer tree is missing, avoid sending "group by depth level".
      return undefined;
    }

    // Some clients have a single polygon with depth 0. In that case, ensure to
    // use it as the level to group by.
    const mostGranularLevel = getMostGranularLevel(divisionTree);
    const shouldReturnMostGranularLevel =
      Number.isInteger(mostGranularLevel) &&
      mostGranularLevel !== undefined &&
      mostGranularLevel < DEFAULT_DEPTH_LEVEL_TO_GROUP_BY;
    if (shouldReturnMostGranularLevel) {
      return mostGranularLevel.toString();
    }

    if (!filters?.division) {
      return undefined;
    }

    const division = getDivision(filters.division, divisionTree);
    if (!division) {
      return undefined;
    }
    // Displaying by children requires to group by more granular depth.
    const hasChildren = division.children_divisions.length > 0;
    const childrenAdditionalDepth = !filters?.displayDivisionSiblings && hasChildren ? 1 : 0;

    return (division.depth + childrenAdditionalDepth).toString();
  }, [filters?.division, filters?.displayDivisionSiblings, divisionTree]);
};
